// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * What an asset's box says, in the one-line's register.
 *
 * Pure text composition over the live and structural passes, so the wording an
 * operator reads is testable without a DOM and every component draws the same
 * words for the same state.
 *
 * **The anatomy is the reference's, not a card's.** A device on a one-line
 * carries a name, one state word, and at most two figures — `UPS1A / Supplying
 * load / 529 kW / SoC 96 %`. It does not carry its equipment type, its nameplate and
 * its readings stacked in a panel; the type is evident from where it sits in
 * the chain and what enclosure it is in, and the nameplate belongs beside the
 * loading it explains. Everything here is capped to that shape, because the
 * previous five-line panel per asset is what made the drawing read as a graph
 * of cards rather than as a schematic.
 *
 * **Provenance survives the compression.** The honesty contract in
 * `docs/power-flow/power-flow-inference.md` requires that a claim never look
 * like a published value. The state word carries it: an inferred state is drawn
 * italic and its rule reaches the accessible name and the hover title, which is
 * how the reference distinguishes `Standby` from a published `Online`. Colour
 * is not used for it, so the distinction survives greyscale.
 *
 * **The vocabulary claims only what the measurement proves.** A UPS exporting
 * power is *supplying load*; whether it does so double-converting, on battery
 * or through its static bypass is a mode the twin does not publish, so the word
 * `Online` — which names one of those modes — is never inferred here. The N+1
 * badge is likewise a *capacity* screen (load against the UPS rating that
 * remains after losing the largest unit), not a proof that the redundancy is
 * available.
 */

import type { PowerFlowVertex } from './powerFlowGraph';
import { isMotorSymbol } from '../config/powerFlowSymbols';
import type { PowerFlowInference } from './powerFlowInference';
import type { PowerFlowLiveState, RedundancyCheck } from './powerFlowLive';
import { formatPowerKw, formatQuantity } from './quantity';

export interface AssetBadge {
  text: string;
  tone: 'ok' | 'warn';
  /** The rule behind the badge, for the hover title and the accessible name. */
  rule?: string;
}

/** The meter drawn on a bus: what a panel meter reports for that run. */
export interface MeterReading {
  volts?: string;
  power?: string;
  current?: string;
}

export interface AssetAnnotation {
  /** The one state word: `Standby`, `Supplying load`, `Running`, `Idle`. */
  state?: string;
  /** True when the state is inferred rather than published. Drawn italic. */
  stateInferred: boolean;
  /** Why the state was inferred, for the accessible name and the hover title. */
  stateRule?: string;
  /** At most two figures, in drawing order. */
  values: string[];
  /** Present on a bus: the meter block beside the bar. */
  meter?: MeterReading;
  badge?: AssetBadge;
  /** True when some of this asset's readings are stale. */
  stale: boolean;
  /**
   * The glyph's fill on a rotating machine: running, stopped, or unknown.
   * Absent on everything else.
   */
  run?: RunState;
}

/** Drawn as fill (running), outline (stopped) or a dashed outline (unknown). */
export type RunState = 'running' | 'stopped' | 'unknown';

/** `3 d`, `5 h`, `12 min`, `40 s`. */
export const formatAge = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const seconds = Math.round(ms / 1000);
  if (seconds < 90) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
};

const percent = (ratio: number): string => `${Math.round(ratio * 100)} %`;

const MEGAWATT_FLOOR = 1000;

/** `8.5 MW` on a bus meter, `529 kW` on a device. */
const meterPower = (kw: number): string =>
  Math.abs(kw) >= MEGAWATT_FLOOR
    ? `${(kw / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} MW`
    : `${kw.toLocaleString('en-US', { maximumFractionDigits: 0 })} kW`;

/**
 * The electrical readout beside a utility feed: volts, current, power and
 * power factor, each only where the twin publishes it (AB#34848). A missing
 * quantity leaves no label behind. Two lines at most, the device cap: V and A
 * share the first, power and PF the second. Power reads export before import,
 * as every source in this file does, so a bidirectional POI meter shows what
 * the site is sending out; where the live pass rated the feed, the figure is
 * its loading (`1.2 MW · 45 %`) so the %-of-rating survives. Frequency is not
 * read — no payload we hold publishes it on a feed.
 */
const feedReadout = (id: string, live: PowerFlowLiveState): string[] => {
  const volts = live.voltsOf.get(id);
  const amps = live.ampsOf.get(id);
  const loading = live.loadingOf.get(id);
  const power = live.exportKwOf.get(id) ?? live.powerKwOf.get(id);
  const pf = live.powerFactorOf.get(id);
  const line = (parts: (string | undefined)[]) => parts.filter(Boolean).join(' · ');
  const powerFigure = loading
    ? `${formatPowerKw(loading.live, loading.unit === 'kW' ? 'W' : 'VA')} · ${percent(loading.ratio)}`
    : power !== undefined
      ? meterPower(power)
      : undefined;
  return [
    line([
      volts !== undefined ? formatQuantity({ base: volts, family: 'V', unit: '' }) : undefined,
      amps !== undefined
        ? `${amps.toLocaleString('en-US', { maximumFractionDigits: 0 })} A`
        : undefined,
    ]),
    line([powerFigure, pf !== undefined ? `PF ${pf.toFixed(2)}` : undefined]),
  ].filter(text => text.length > 0);
};

export interface AnnotateOptions {
  /** Epoch ms, for ages. */
  now: number;
  /** Human name for an asset id, for "selects TXMR-1". */
  nameOf: (_id: string) => string;
  /** When false, no inferred state and no badge is produced. */
  showInferred?: boolean;
}

/**
 * The age of the stale reading a source's own standby verdict rests on.
 *
 * Read off the source's own links (`${id}->…`), never off the site-wide oldest
 * stale point: a genset whose output was read an hour ago must not be captioned
 * with the three-day age of an unrelated RPP's voltage. When the verdict is
 * flagged stale but no per-link timestamp survived, the caption says stale
 * without inventing an age.
 */
const standbyEvidenceAge = (id: string, live: PowerFlowLiveState, now: number): string => {
  const prefix = `${id}->`;
  let oldest: number | undefined;
  for (const [linkId, at] of Array.from(live.staleEvidenceAtOf.entries())) {
    if (!linkId.startsWith(prefix)) continue;
    if (oldest === undefined || at < oldest) oldest = at;
  }
  if (oldest !== undefined) return `, output read ${formatAge(now - oldest)} ago`;
  const flagged = Array.from(live.staleEvidenceLinkIds).some(linkId => linkId.startsWith(prefix));
  return flagged ? ', output reading stale' : '';
};

/** What the N+1 badge screens, so a reader never mistakes it for availability. */
const redundancyRule = (check: RedundancyCheck): string =>
  `capacity screen: ${formatPowerKw(check.load)} load against ${formatPowerKw(
    check.spareCapacity
  )} — the ${check.upsIds.length} UPS ratings (${formatPowerKw(
    check.totalRating
  )}) minus the largest (${formatPowerKw(check.largestRating)}); not a proof of availability`;

export const annotateAsset = (
  vertex: PowerFlowVertex,
  live: PowerFlowLiveState,
  inference: PowerFlowInference,
  options: AnnotateOptions
): AssetAnnotation => {
  const { id } = vertex;
  const showInferred = options.showInferred ?? true;
  const values: string[] = [];
  const stale = live.staleKindsOf.has(id);

  let state: string | undefined;
  let stateInferred = false;
  let stateRule: string | undefined;
  let badge: AssetBadge | undefined;

  // --- The state word ------------------------------------------------------
  // One word, and the most consequential fact about the asset right now.
  // Every branch below except the motor's is an inference, and an inference
  // is withheld — not merely restyled — when the reader turns the tier off.
  if (live.standbyIds.has(id)) {
    if (showInferred) {
      state = 'Standby';
      stateInferred = true;
      stateRule = `inferred from zero export${standbyEvidenceAge(id, live, options.now)}`;
    }
  } else if (vertex.role === 'source' && live.runningOf.get(id) === true) {
    if (showInferred) {
      state = 'Carrying load';
      stateInferred = true;
      stateRule = 'inferred from measured export';
    }
  } else if (vertex.symbol === 'ups') {
    // A UPS that is passing power is supplying its load. That is all a positive
    // export proves — a unit on battery or on static bypass exports too — and
    // the twin publishes no UPS mode, so the word is read off the export and
    // says so. `Online` is a mode word and is not claimed.
    const exported = live.exportKwOf.get(id);
    if (exported !== undefined && showInferred) {
      state = exported > 1 ? 'Supplying load' : 'Idle';
      stateInferred = true;
      stateRule = 'inferred from measured output';
    }
  } else if (isMotorSymbol(vertex.symbol)) {
    // A published run-status is a measurement and stays with the tier off. A
    // verdict read off the draw instead is an inference and is marked as one.
    const running = live.runningOf.get(id);
    if (running !== undefined) {
      if (live.runInferredIds.has(id)) {
        if (showInferred) {
          state = running ? 'Running' : 'Idle';
          stateInferred = true;
          stateRule = 'inferred from measured draw';
        }
      } else {
        state = running ? 'Running' : 'Idle';
      }
    }
  }

  // --- The glyph's run state -------------------------------------------------
  // Rotating machines fill their glyph when running and dash it when nothing
  // says either way. Separate from the state word so an idle machine (hollow)
  // and a machine with no reading (dashed) never look alike.
  let run: RunState | undefined;
  if (isMotorSymbol(vertex.symbol) || vertex.symbol === 'generator') {
    const running = live.runningOf.get(id);
    run = running === undefined ? 'unknown' : running ? 'running' : 'stopped';
  }

  // --- The figures ---------------------------------------------------------
  // Two lines. The loading figure always leads; the second line is the
  // stored-energy figure when the asset has one (SoC on a UPS or BESS, fuel on
  // a genset) because that is the number an operator needs next, and the
  // nameplate it is loaded against otherwise.
  const stored: string[] = [];
  const fuel = live.fuelOf.get(id);
  if (fuel !== undefined) stored.push(`fuel ${Math.round(fuel)} %`);
  const soc = live.socOf.get(id);
  if (soc !== undefined) stored.push(`SoC ${Math.round(soc)} %`);

  const loading = live.loadingOf.get(id);
  if (vertex.symbol === 'utility') {
    values.push(...feedReadout(id, live));
  } else if (loading) {
    const family = loading.unit === 'kW' ? 'W' : 'VA';
    const fedBoard = loading.basis === 'downstream' ? ' · fed board' : '';
    const rated = `of ${formatQuantity({ base: loading.rated * 1000, family, unit: '' })}`;
    if (stored.length > 0) {
      // The rated line is displaced, so the basis note it carried moves up.
      values.push(`${formatPowerKw(loading.live, family)} · ${percent(loading.ratio)}${fedBoard}`);
      values.push(...stored);
    } else {
      values.push(`${formatPowerKw(loading.live, family)} · ${percent(loading.ratio)}`);
      values.push(`${rated}${fedBoard}`);
    }
  } else {
    const draw = live.powerKwOf.get(id);
    const exported = live.exportKwOf.get(id);
    const figure =
      vertex.symbol === 'ups' && exported !== undefined
        ? exported
        : vertex.role === 'source'
          ? exported
          : draw;
    if (figure !== undefined) values.push(meterPower(figure));
    values.push(...stored);
  }

  // --- The meter on a bus --------------------------------------------------
  let meter: MeterReading | undefined;
  if (vertex.role === 'bus') {
    const volts = live.voltsOf.get(id);
    const amps = live.ampsOf.get(id);
    const power = live.powerKwOf.get(id);
    if (volts !== undefined || amps !== undefined || power !== undefined) {
      meter = {
        ...(volts !== undefined
          ? { volts: formatQuantity({ base: volts, family: 'V', unit: '' }) }
          : {}),
        ...(power !== undefined ? { power: meterPower(power) } : {}),
        ...(amps !== undefined
          ? { current: `${amps.toLocaleString('en-US', { maximumFractionDigits: 0 })} A` }
          : {}),
      };
    }
    // A bus states its measurements in the meter, never twice.
    values.length = 0;
  }

  if (!showInferred) {
    return { state, stateInferred: false, values: values.slice(0, 2), meter, stale, run };
  }

  // --- Findings worth a badge ---------------------------------------------
  const selected = live.selectedSourceOf.get(id);
  if (inference.sourceSelectionOf.has(id) && selected && selected.length > 1) {
    badge = { text: 'both sources live', tone: 'warn' };
  }

  const redundancy = live.redundancyOf.get(id);
  if (redundancy) {
    badge = redundancy.holds
      ? { text: 'N+1 capacity holds', tone: 'ok', rule: redundancyRule(redundancy) }
      : {
          text: `N+1 capacity marginal at ${percent(redundancy.ratio)}`,
          tone: 'warn',
          rule: redundancyRule(redundancy),
        };
  }

  const branch = inference.branchOf.get(id);
  // Never over a capacity finding: N+1 is the one an operator acts on.
  if (branch && !branch.labelAgrees && !(redundancy && !redundancy.holds)) {
    badge = { text: 'label mismatch', tone: 'warn' };
  }

  return { state, stateInferred, stateRule, values: values.slice(0, 2), meter, badge, stale, run };
};

/**
 * The sub-label under a bus, naming what it feeds.
 *
 * The reference writes `Building Power` and `Dual Input Loads` beneath a bus
 * rather than labelling every drop, and it is the cheapest way to say what a
 * board is for. Derived from the branch class, so it is never a guess.
 */
export const busSublabel = (
  vertex: PowerFlowVertex,
  inference: PowerFlowInference
): string | undefined => {
  if (vertex.role !== 'bus') return undefined;
  switch (inference.branchOf.get(vertex.id)?.branchClass) {
    case 'critical':
      return 'Critical load';
    case 'mechanical':
      return 'Mechanical load';
    default:
      return undefined;
  }
};
